import React from 'react';
import { User, Building2, FileText, Phone, ShoppingBag, LogOut, ChevronRight, MessageCircle } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { STOCKIST } from '@/data/mockData';

const SWISHX_LOGO = 'https://customer-assets.emergentagent.com/job_womens-health-app-16/artifacts/krehdyqm_swishx-updated-orng-logo%20%281%29.png';

function IconBadge({ Icon }) {
  return (
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: '#FFF0EB' }}
    >
      <Icon size={18} color="#FF5722" />
    </div>
  );
}

function ActivityRow({ Icon, title, subtitle, testId, onPress }) {
  return (
    <button
      data-testid={testId}
      onClick={onPress}
      className="flex items-center gap-3 py-3.5 w-full tap-scale text-left border-b border-gray-100 last:border-0"
    >
      <IconBadge Icon={Icon} />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-gray-900 text-sm">{title}</div>
        <div className="text-gray-400 text-xs mt-0.5 leading-snug">{subtitle}</div>
      </div>
      <ChevronRight size={18} color="#FF5722" className="flex-shrink-0" />
    </button>
  );
}

export default function ProfileScreen() {
  const { navigate } = useApp();

  return (
    <div className="min-h-screen bg-[#F5F6FA] pb-8">

      {/* Header */}
      <div className="bg-white px-4 pt-5 pb-3 border-b border-gray-100 flex items-center justify-center">
        <h1
          className="font-black text-gray-900 uppercase"
          style={{ fontSize: '15px', letterSpacing: '0.12em' }}
        >
          PROFILE
        </h1>
      </div>

      <div className="px-4 pt-4 flex flex-col gap-3">

        {/* Profile identity card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4">
          <div
            className="w-16 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: '#FFF0EB' }}
          >
            <User size={30} color="#FF5722" />
          </div>
          <div>
            <div className="font-black text-gray-900 text-base leading-tight">{STOCKIST.name}</div>
            <div className="text-gray-400 text-sm mt-0.5">Stockist ID: {STOCKIST.accountCode}</div>
            <div className="mt-1.5">
              <span
                className="text-green-600 font-bold text-xs px-2.5 py-0.5 rounded-full"
                style={{ background: '#DCFCE7' }}
              >
                Verified
              </span>
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="font-black text-gray-900 text-base mb-4">Business Details</div>
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <IconBadge Icon={Building2} />
              <div>
                <div className="font-bold text-gray-900 text-sm">Business Address</div>
                <div className="text-gray-400 text-sm mt-0.5 leading-snug">
                  42 Medical Hub, SG Highway,<br />Ahmedabad — 380054
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <IconBadge Icon={FileText} />
              <div>
                <div className="font-bold text-gray-900 text-sm">GST Information</div>
                <div className="text-gray-400 text-sm mt-0.5">GSTIN: 24AANFA5678B1ZS</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <IconBadge Icon={Phone} />
              <div>
                <div className="font-bold text-gray-900 text-sm">Contact Information</div>
                <div className="text-gray-400 text-sm mt-0.5">{STOCKIST.phone}</div>
              </div>
            </div>
          </div>
        </div>

        {/* My Activity */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="font-black text-gray-900 text-base mb-1">My Activity</div>
          <ActivityRow
            Icon={ShoppingBag}
            title="Order & Request"
            subtitle="Send medicine requests to stockist"
            testId="profile-order-request"
            onPress={() => navigate('PRODUCTS')}
          />
          <ActivityRow
            Icon={FileText}
            title="Purchase History"
            subtitle="View your past purchase records"
            testId="profile-purchase-history"
            onPress={() => navigate('INVOICES')}
          />
          <ActivityRow
            Icon={FileText}
            title="My Invoice"
            subtitle="View all uploaded invoices"
            testId="profile-my-invoice"
            onPress={() => navigate('INVOICES')}
          />
        </div>

        {/* Need help? */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="font-black text-gray-900 text-base mb-1">Need help?</div>
          <div className="text-gray-400 text-sm mb-4 leading-snug">
            Connect with your RM for ordering and invoice support.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="profile-call-rm"
              className="border border-gray-200 rounded-xl py-3.5 flex items-center justify-center gap-2 tap-scale"
            >
              <Phone size={16} color="#FF5722" />
              <span className="font-bold text-sm" style={{ color: '#FF5722' }}>Call RM</span>
            </button>
            <button
              data-testid="profile-chat-rm"
              className="border border-gray-200 rounded-xl py-3.5 flex items-center justify-center gap-2 tap-scale"
            >
              <MessageCircle size={16} color="#FF5722" />
              <span className="font-bold text-sm" style={{ color: '#FF5722' }}>Chat RM</span>
            </button>
          </div>
        </div>

        {/* Powered by SwishX */}
        <div className="flex items-center justify-center gap-2 py-1">
          <span className="text-gray-400 text-sm font-medium">Powered by</span>
          <img
            src={SWISHX_LOGO}
            alt="SwishX"
            className="h-7 w-auto"
            style={{ mixBlendMode: 'multiply' }}
          />
        </div>

        {/* Logout */}
        <button
          data-testid="profile-logout-btn"
          onClick={() => navigate('HOME')}
          className="w-full rounded-2xl py-4 font-bold text-base tap-scale flex items-center justify-center gap-2"
          style={{ border: '1.5px solid #FFCCBC', background: '#FFF8F6', color: '#FF5722' }}
        >
          <LogOut size={18} color="#FF5722" />
          Logout
        </button>

      </div>
    </div>
  );
}
